/**
 * The invite id an `/invite/accept?inviteId=…` link carried into the console.
 *
 * The invite email's web link (platform `functions/src/lib/mail.ts`) points at the console origin with
 * the invite id in the query. Hosting rewrites every path to `/index.html`, so that link already serves
 * the console — but the console routes off `location.hash`, so the path means nothing to it and a
 * reload would re-enter the same dead path. So on boot the id is lifted into `sessionStorage` and the
 * address bar is put back on the root, keeping every other query parameter (`?lan=`, `?local-console=`)
 * and the hash intact.
 *
 * It is `sessionStorage`, not `localStorage`: an invite is a one-visit errand. It survives the sign-in
 * round trip — including a Google popup and the wait for an email verification link — and does not
 * outlive the tab.
 *
 * The id is consumed after a VERIFIED sign-in, where the server-side `claimPendingInvites` decides what
 * it is actually worth: the console never claims anything itself, it only remembers which venue the
 * person came for so that venue is the one they land in.
 */

const STORAGE_KEY = 'nuance-venue:pending-invite';
const ACCEPT_PATH = '/invite/accept';

/** sessionStorage is absent in some embedded/private contexts; a missing store is simply no invite. */
function store(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * Lift the invite id out of the URL, put the address bar back on the console root, and return the id
 * this tab is carrying (from this visit or an earlier one in the same tab). Returns '' when there is none.
 */
export function capturePendingInvite(): string {
  const url = new URL(window.location.href);
  const path = url.pathname.replace(/\/+$/, '');
  const fromLink = path === ACCEPT_PATH ? (url.searchParams.get('inviteId') ?? '').trim() : '';

  if (fromLink) {
    try {
      store()?.setItem(STORAGE_KEY, fromLink);
    } catch {
      // A store that refuses writes just means the id lives for this page load only.
    }
  }

  // Any path but the root is a deep link no console screen serves. Normalising it means a reload lands
  // on the console instead of re-entering the path, and the hash router sees the URL it expects.
  if (url.pathname !== '/' || url.searchParams.has('inviteId')) {
    url.searchParams.delete('inviteId');
    url.pathname = '/';
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  return fromLink || pendingInviteId();
}

/** The invite id this tab is carrying, if any. */
export function pendingInviteId(): string {
  try {
    return store()?.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Forget the invite — it has been claimed, or the person signed out without taking it up. */
export function clearPendingInvite(): void {
  try {
    store()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to forget if the store is unavailable.
  }
}

export interface ClaimedInvite {
  inviteId: string;
  venueId: string;
  role: string;
}

/**
 * The venue to land in after a claim: the one this tab's invite was for, else the first venue claimed.
 *
 * Someone arriving from an invite link expects to open the venue that invited them, not whichever venue
 * happens to sort first in their list. When the remembered invite is not among the claims (it was for
 * another address, already accepted, or expired) any venue claimed in the same pass is still the venue
 * they just gained, so it wins over the stored preference; if nothing was claimed, nothing is selected.
 */
export function venueToSelectAfterClaim(claimed: ClaimedInvite[], inviteId: string): string {
  if (claimed.length === 0) return '';
  const exact = claimed.find((invite) => invite.inviteId === inviteId);
  return exact?.venueId ?? claimed[0]!.venueId;
}
